import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';
import { actions, type AuthResponse, events, getFieldErrorMessage, views } from '~/vendor/cx-web/feature-auth';
import { CX_LOGIN_SUCCESS } from '~/vendor/cx-web/feature-auth/events';
import { trackLoginFailure, trackLoginSuccess } from '~/vendor/cx-web/feature-auth/tracking';
import { trackEvent } from '~/vendor/cx-web/feature-tracking/dataLayer';
import { REPUB_CONFIG } from '~/vendor/cx-web/repub-config';
import { isValidEmail } from '~/vendor/cx-web/utils';
import { Button } from '~/vendor/cxy/components/Button';
import { Checkbox } from '~/vendor/cxy/components/Checkbox';
import { Input } from '~/vendor/cxy/components/Input';

import { type Repub } from '~/vendor/cx-modules/constants';

import { type OpenAtViewOptions, type View, VIEW_FORGOT_PASSWORD, VIEW_SIGNUP } from '../../../auth-modal-provider';

import styles from './styles/LoginForm.module.scss';

export type LoginFormProps = {
  authBasePath?: string;
  close: () => void;
  openAtView: (view: View, options?: OpenAtViewOptions) => void;
  repub: Repub;
  repubPath: string;
};

export const LoginForm = ({ authBasePath = '/plus/auth', close, openAtView, repub, repubPath }: LoginFormProps) => {
  const fetcher = useFetcher<AuthResponse>();
  const loginSuccessRef = useRef(false);
  const [showErrorState, setShowErrorState] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation(['label']);

  const data = fetcher.data;
  const hasErrorMessage = data?.message && !data?.success;

  // handle local error
  useEffect(() => {
    if (fetcher.state === 'idle' && (data?.fieldErrors || hasErrorMessage)) {
      setShowErrorState(true);
    }
  }, [data?.fieldErrors, hasErrorMessage, data?.message, fetcher.state]);

  // handle api success
  useEffect(() => {
    if (data?.success && !loginSuccessRef.current) {
      loginSuccessRef.current = true;

      trackLoginSuccess('auth_modal', { repub, userId: data.userId! });

      window.dispatchEvent(new CustomEvent(CX_LOGIN_SUCCESS, { detail: data }));

      close();
    }
  }, [close, data, openAtView, repub]);

  // handle api error
  useEffect(() => {
    if (fetcher.state === 'idle' && hasErrorMessage && data) {
      trackLoginFailure('auth_modal', {
        error: data.message ?? '',
        repub,
      });
    }
  }, [fetcher.state, hasErrorMessage, data, repub]);

  const onChange = () => {
    setShowErrorState(false);
  };

  return (
    <fetcher.Form
      action={`${repubPath}${authBasePath}/login`}
      className={styles.loginForm}
      data-testid="login-form"
      method="post"
      onSubmit={() => {
        trackEvent(events.LOG_IN_ATTEMPT, {
          eventAction: actions.LOGIN,
          method: REPUB_CONFIG[repub]?.tracking.authProvider,
          view: views.LOGIN,
        });
      }}
      ref={formRef}
    >
      <Input
        aria-describedby="email-error"
        autoComplete="email"
        errors={
          showErrorState ? ([getFieldErrorMessage(data?.fieldErrors?.email)].filter(Boolean) as string[]) : undefined
        }
        id="email"
        key="email"
        name="email"
        onChange={onChange}
        placeholder={t('CX_AUTH_LOGIN_PLACEHOLDER_EMAIL')}
        type="email"
      />
      <Input
        aria-describedby="password-error"
        autoComplete="current-password"
        errors={
          showErrorState ? ([getFieldErrorMessage(data?.fieldErrors?.password)].filter(Boolean) as string[]) : undefined
        }
        id="password"
        key="password"
        name="password"
        onChange={onChange}
        placeholder={t('CX_AUTH_LOGIN_PLACEHOLDER_PASSWORD')}
        type="password"
      />
      {hasErrorMessage && showErrorState && (
        <div className={styles.errorMessage} id="generic-error">
          {data.message}
        </div>
      )}
      <div className={styles.buttonSection}>
        <Button isLoading={fetcher.state !== 'idle'} size="large" type="submit" variant="secondary">
          {t('CX_AUTH_LOGIN')}
        </Button>
      </div>
      <div className={styles.checkboxSection}>
        <Checkbox defaultChecked id="remember" name="remember" shouldHighlightOnChecked={false} />
        <label className={styles.checkboxLabel} htmlFor="keep-logged-in">
          {t('CX_AUTH_KEEP_ME_LOGGED_IN')}
        </label>
        <a
          className={styles.forgotPassword}
          href={`${repubPath}${authBasePath}/forgot-password`}
          onClick={e => {
            e.preventDefault();
            // Read email value from the uncontrolled input at click time. Works with password managers
            // and autofill (which update input.value directly without firing onChange), mirroring
            // SignupForm's submittedEmailRef pattern.
            const emailValue = formRef.current ? String(new FormData(formRef.current).get('email') ?? '') : '';
            const prefillEmail = isValidEmail(emailValue) ? emailValue : undefined;
            openAtView(VIEW_FORGOT_PASSWORD, { prefillEmail });
          }}
        >
          {t('CX_AUTH_FORGOT_PASSWORD_QUESTION')}
        </a>
      </div>
      <p className={styles.signupText}>
        {t('CX_AUTH_DONT_HAVE_ACCOUNT')}{' '}
        <a
          className={styles.signupLink}
          href={`${repubPath}${authBasePath}/signup`}
          onClick={e => {
            e.preventDefault();
            openAtView(VIEW_SIGNUP);
          }}
        >
          {t('CX_AUTH_SIGNUP')}
        </a>
      </p>
    </fetcher.Form>
  );
};

LoginForm.displayName = 'LoginForm';
